import { useEffect, useState } from 'react';
import './App.css';
import AppHeader from './components/AppHeader';
import ContactStep from './components/ContactStep';
import QuestionScreen from './components/QuestionScreen';
import ReportScreen from './components/ReportScreen';
import StandardReportScreen from './components/StandardReportScreen';
import StartScreen from './components/StartScreen';
import Admin from './components/Admin';
import HubHome from './components/HubHome';
import OtpModal from './components/OtpModal';
import { resolveAssessment, resolvePublishedAssessment } from './config/assessments';
import { publicCatalog } from './lib/assessmentApi';
import { requestEmailCode, verifyEmailCode, verifyConfigured } from './lib/verify';
import { configureTracking, trackAssessment } from './lib/analytics';
import { assessmentConfig } from './config/site';
import { reportConfig } from './config/report';
import { getSessionContext } from './lib/assessment';
import { validateAnswers } from './lib/report';
import { createAssessmentReportAttachment } from './lib/assessmentReport';
import { loadDraft, saveDraft, clearDraft, postAssessment, backendConfigured } from './lib/storage';

function App() {
  const [assessment,setAssessment] = useState(() => resolveAssessment(window.location.pathname));
  const [catalogLoading,setCatalogLoading] = useState(!resolveAssessment(window.location.pathname) && /^\/[as]\//.test(window.location.pathname));
  const [catalogError,setCatalogError] = useState('');
  const [draft] = useState(() => assessment?loadDraft(assessment.id):null);
  const [session] = useState(()=>draft?.session || getSessionContext());
  const [step,setStep] = useState(draft?.step ?? -1);
  const [answers,setAnswers] = useState(draft?.answers || {});
  const [contact,setContact] = useState(draft?.contact || null);
  const [delivery,setDelivery] = useState('');
  const [saving,setSaving] = useState(false);
  const [error,setError] = useState('');
  const [otp,setOtp] = useState(null);
  const [otpError,setOtpError] = useState('');
  const [pendingContact,setPendingContact] = useState(null);
  const questions = assessment?.questions || assessmentConfig.questions;
  useEffect(()=>{ if(assessment) configureTracking(assessment); },[assessment]);
  useEffect(()=>{
    if(assessment||!/^\/[as]\//.test(window.location.pathname))return;
    let cancelled=false;
    publicCatalog().then(catalog=>{
      if(cancelled)return;
      const found=resolvePublishedAssessment(window.location.pathname,catalog);
      if(found){setAssessment(found);const saved=loadDraft(found.id);
        if(saved){setStep(saved.step??-1);setAnswers(saved.answers||{});setContact(saved.contact||null);}}
      setCatalogLoading(false);
    }).catch(()=>{if(!cancelled){setCatalogError('Assessment catalogue is temporarily unavailable.');setCatalogLoading(false);}});
    return ()=>{cancelled=true;};
  },[assessment]);
  const isReport = step > questions.length;
  const isContact = step === questions.length;

  useEffect(()=>{ if (!isReport && assessment) saveDraft({session,step,answers,contact},assessment.id); },[session,step,answers,contact,isReport,assessment]);
  const saveAnswer=(id,value)=> {
    setAnswers(current=>({...current,[id]:value}));
    trackAssessment(assessment?.id,'question_answered',{question_id:id});
  };
  const submit=async details=>{
    setError('');
    if(!validateAnswers(answers,questions)){setError('Please complete all assessment questions first.');return;}
    if(!details.firstName.trim()||!details.businessName.trim()||!details.phone.trim()||!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email.trim())){setError('Please provide your name, business and a valid email.');return;}
    if(!details.reportConsent){setError('Please consent to receiving your report by email before continuing.');return;}
    if(!verifyConfigured||!backendConfigured){setError('Email verification and secure reporting are not configured yet. Please contact BPA Solutions.');return;}
    setSaving(true);
    try {
      const response=await requestEmailCode(details.email);
      if(!response.challengeId)throw new Error('Verification could not be started.');
      setPendingContact(details);
      setOtp({challengeId:response.challengeId,email:details.email,expiresAt:Date.now()+Math.max(0,Number(response.expiresIn)||300)*1000,resendAt:Date.now()+Math.max(0,Number(response.resendAfter)||45)*1000});
      setOtpError('');
      trackAssessment(assessment?.id,'otp_sent');
    } catch(e){setError(e.message||'Unable to send verification code.');}
    finally{setSaving(false);}
  };
  const confirmOtp=async code=>{
    if(saving||!otp)return;
    setSaving(true);setOtpError('');
    let verified;
    try {
      verified=await verifyEmailCode(otp.challengeId,code);
      if(!verified.verified||!verified.verificationToken)throw new Error(verified.error||'The code was not accepted.');
    } catch(e) {
      setOtpError(e.message||'Unable to verify your code. Please retry.');
      setSaving(false);
      return;
    }
    // Verification succeeded. Never put a downstream email gateway failure back into
    // the OTP modal: this token/challenge may be single-use after successful verification.
    const details={...pendingContact,verificationToken:verified.verificationToken};
    setContact(pendingContact);
    setOtp(null);
    setPendingContact(null);
    setStep(questions.length+1);
    clearDraft(assessment?.id);
    setDelivery('Your email is verified. Preparing report delivery…');
    trackAssessment(assessment?.id,'email_verified');
    try{
      const pdf=await createAssessmentReportAttachment(assessment,answers,details);
      const result=await postAssessment({assessmentId:assessment.id,assessmentVersion:assessment.publishedVersion||1,session,answers,contact:details,pdf});
      if(!result.emailed && !result.queued)throw new Error('Report email delivery could not be accepted.');
      setDelivery('Your personalised PDF has been queued for email delivery. You can also download it now.');
    }catch(e){
      setDelivery('Your email was verified successfully, but we could not send the report email. You can still view and download your Health Report below. Please contact BPA Solutions if you need it emailed.');
      trackAssessment(assessment?.id,'report_email_failed',{reason:'gateway_delivery'});
    }finally{
      setSaving(false);
      trackAssessment(assessment?.id,'report_viewed');
    }
  };
  const resendOtp=async()=>{
    setSaving(true);setOtpError('');
    try {const result=await requestEmailCode(otp.email);if(!result.challengeId)throw new Error('Unable to resend.');setOtp({...otp,challengeId:result.challengeId,expiresAt:Date.now()+Math.max(0,Number(result.expiresIn)||300)*1000,resendAt:Date.now()+Math.max(0,Number(result.resendAfter)||45)*1000});}
    catch(e){setOtpError(e.message||'Unable to resend code.');}
    finally{setSaving(false);}
  };
  const back=()=>setStep(p=>Math.max(-1,p-1));
  if(window.location.pathname.startsWith('/admin'))return <Admin/>;
  if(window.location.pathname==='/' || window.location.pathname==='')return <HubHome/>;
  if(!assessment) return <div className="app-shell"><main className="main-content"><section className="start-screen"><h1>{catalogLoading?'Opening assessment…':'Assessment not found'}</h1><p>{catalogLoading?'Loading the published assessment.':catalogError||'Please check your assessment link.'}</p><a href="/">Return to BPA Solutions assessments</a></section></main></div>;
  return <div className="app-shell" data-session-id={session.sessionId}>
    <div className="print-hide"><AppHeader showBack={step>=0 && !isReport} onBack={back} progress={step>=0 ? Math.min(step+1,questions.length):null} total={questions.length}/></div>
    <main className={isReport?'main-content report-main':'main-content'}>
      {step===-1 && <StartScreen assessment={assessment} onStart={()=>{trackAssessment(assessment.id,'assessment_started');setStep(0);}}/>}
      {step>=0 && step<questions.length && <QuestionScreen key={questions[step].id} question={questions[step]} index={step}
        answer={answers[questions[step].id]} onAnswer={saveAnswer} onNext={()=>setStep(s=>Math.min(s+1,questions.length))}/>}
      {isContact && <ContactStep onSubmit={submit} saving={saving} error={error}/>}
      {isReport && contact && (assessment.reportType==='timesheet'
        ? <ReportScreen assessment={assessment} answers={answers} contact={contact} delivery={delivery}/>
        : <StandardReportScreen assessment={assessment} answers={answers} contact={contact} delivery={delivery}/>)}
    </main>
    {otp&&<OtpModal email={otp.email} expiresAt={otp.expiresAt} resendAt={otp.resendAt} busy={saving} error={otpError} onCodeChange={()=>setOtpError('')} onVerify={confirmOtp} onResend={resendOtp} onClose={()=>{setOtp(null);setPendingContact(null);}}/>}
    {error && !isContact && <p role="status" className="save-warning print-hide">{error}</p>}
    <footer className="site-footer bpa-assessment-footer"><div className="bpa-footer-contact"><strong>BPA Solutions</strong><a href={`mailto:${reportConfig.contactEmail}`}>{reportConfig.contactEmail}</a><a href="tel:+61425228778">{reportConfig.contactPhone}</a><span>{reportConfig.contactAddress}</span><a href={reportConfig.websiteUrl} target="_blank" rel="noopener noreferrer">www.bpasolutions.au</a></div><div className="bpa-footer-links"><a href={reportConfig.privacyUrl} target="_blank" rel="noopener noreferrer">Privacy Policy</a><span>Indicative results only. Not legal, financial or award advice.</span></div></footer>
  </div>;
}
export default App;